import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";
import { missingServiceNameKey, type MissingServicesImport } from "@/lib/quote-missing-services";
import { quoteTextPlausibilityResponseSchema, type QuoteTextPosition,
  type QuoteTextPlausibilityResponse } from "@shared/quote-text-plausibility";

const categories = [
  { key: "fehlt", label: "Fehlt", color: "border-red-300 bg-red-50 text-red-950", dot: "bg-red-600" },
  { key: "unklar", label: "Unklar", color: "border-orange-300 bg-orange-50 text-orange-950", dot: "bg-orange-500" },
  { key: "nicht_beschrieben", label: "Nicht beschrieben", color: "border-gray-300 bg-gray-50 text-gray-900", dot: "bg-gray-500" },
  { key: "teil_hinweis", label: "Teil-Hinweis", color: "border-gray-300 bg-gray-50 text-gray-900", dot: "bg-gray-500" },
] as const;

function errorMessage(error: unknown): string {
  if (!(error instanceof Error)) return "Die Prüfung konnte nicht ausgeführt werden.";
  const raw = error.message.replace(/^\d{3}:\s*/, "");
  if (raw === "Ungültiges Prüfergebnis") return "Die KI hat kein gültiges Prüfergebnis geliefert. Bitte erneut versuchen.";
  try {
    const message = JSON.parse(raw).message;
    return typeof message === "string" ? message : "Die Prüfung konnte nicht ausgeführt werden.";
  }
  catch { return "Die Prüfung konnte nicht ausgeführt werden. Bitte Verbindung prüfen und erneut versuchen."; }
}

const normalizeTitle = missingServiceNameKey;

type ImportSection = { id: string; title: string };

type QuoteTextPlausibilityProps = {
  description: string;
  positions: QuoteTextPosition[];
  onImportMissing?: (request: MissingServicesImport) => void | Promise<void>;
  importSections?: ImportSection[];
};

export function QuoteTextPlausibility({ description, positions, onImportMissing, importSections }: QuoteTextPlausibilityProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [result, setResult] = useState<QuoteTextPlausibilityResponse | null>(null);
  const [snapshot, setSnapshot] = useState<{ text: string; positions: QuoteTextPosition[] } | null>(null);
  const [selectedMissing, setSelectedMissing] = useState<Set<number>>(() => new Set());
  const [sectionId, setSectionId] = useState("");
  const [teil, setTeil] = useState<MissingServicesImport["teil"] | "">("");
  const [, setImportedVersion] = useState(0);
  const controllerRef = useRef<AbortController | null>(null);
  const importingRef = useRef(false);
  const importedTitlesRef = useRef<Set<string>>(new Set());
  const resultRunRef = useRef(0);
  useEffect(() => () => { controllerRef.current?.abort(); controllerRef.current = null; }, []);
  const close = () => {
    resultRunRef.current += 1;
    controllerRef.current?.abort();
    controllerRef.current = null;
    setSelectedMissing(new Set());
    setLoading(false); setOpen(false); setResult(null); setSnapshot(null); setError(null); setImportError(null);
  };
  const check = async () => {
    if (controllerRef.current || !description.trim()) return;
    resultRunRef.current += 1;
    setSelectedMissing(new Set());
    setImportError(null);
    const controller = new AbortController();
    controllerRef.current = controller;
    const input = { text: description, positions: positions.filter((p) => !p.variant) };
    setSnapshot(input); setOpen(true); setLoading(true); setError(null); setResult(null);
    try {
      const response = await apiRequest("POST", "/api/quotes/plausibility", input, controller.signal);
      const parsed = quoteTextPlausibilityResponseSchema.safeParse(await response.json());
      if (!parsed.success) throw new Error("Ungültiges Prüfergebnis");
      if (!controller.signal.aborted) setResult(parsed.data);
    } catch (failure) {
      if (!controller.signal.aborted) setError(errorMessage(failure));
    } finally {
      if (controllerRef.current === controller) { controllerRef.current = null; setLoading(false); }
    }
  };
  const rejected = result?.nicht_belegbar ?? [];
  const hasFindings = result && categories.some(({ key }) => result[key].length > 0);
  const empty = result && !hasFindings && rejected.length === 0;
  const stale = snapshot && JSON.stringify(snapshot) !== JSON.stringify({
    text: description, positions: positions.filter((p) => !p.variant),
  });
  const canImport = typeof onImportMissing === "function" && Boolean(importSections?.length);
  const resolvedSectionId = importSections?.length === 1 ? importSections[0].id : sectionId;
  const existingTitles = new Set(positions.map((position) => normalizeTitle(position.kurztext)));
  const missingStatus = (entry: { leistung: string }) => {
    const normalized = normalizeTitle(entry.leistung);
    if (importedTitlesRef.current.has(normalized)) return "Bereits übernommen";
    if (existingTitles.has(normalized)) return "Bereits im Angebot";
    return null;
  };
  const seenSelectedTitles = new Set<string>();
  const selectedEntries = result?.fehlt.filter((entry, index) => {
    if (!selectedMissing.has(index) || missingStatus(entry)) return false;
    const title = normalizeTitle(entry.leistung);
    if (!title || seenSelectedTitles.has(title)) return false;
    seenSelectedTitles.add(title);
    return true;
  }) ?? [];
  const selectedCount = selectedEntries.length;
  const submitMissing = async () => {
    if (importingRef.current || !onImportMissing || !importSections?.length || stale || loading || !resolvedSectionId || !teil || !result) return;
    const entries = selectedEntries.map(({ leistung, zitat }) => ({ leistung, zitat }));
    if (entries.length === 0) return;
    const request: MissingServicesImport = { entries, sectionId: resolvedSectionId, teil };
    const titlesToMark = entries.map(({ leistung }) => normalizeTitle(leistung));
    const submissionRun = resultRunRef.current;
    importingRef.current = true;
    setImporting(true);
    setImportError(null);
    try {
      await onImportMissing(request);
      titlesToMark.forEach((title) => importedTitlesRef.current.add(title));
      setImportedVersion((version) => version + 1);
      if (resultRunRef.current === submissionRun) {
        setSelectedMissing((current) => {
          const next = new Set(current);
          result.fehlt.forEach((entry, index) => {
            if (titlesToMark.includes(normalizeTitle(entry.leistung))) next.delete(index);
          });
          return next;
        });
      }
    } catch {
      if (resultRunRef.current === submissionRun) setImportError("Die Leistungen konnten nicht ins LV übernommen werden. Bitte erneut versuchen.");
    } finally {
      importingRef.current = false;
      setImporting(false);
    }
  };
  return <>
    <section className="space-y-2 rounded-lg border p-4" aria-label="KI-Abgleich von Beschreibung und Positionen">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-semibold">Leistungsbeschreibung mit Positionen abgleichen</h4>
          <p className="text-sm text-muted-foreground">Separate KI-Prüfung auf fehlende Leistungen und Widersprüche. Die Prüfung bleibt schreibgeschützt; nur ausdrücklich ausgewählte fehlende Leistungen können ins LV übernommen werden. Das blockiert keinen Export.</p>
        </div>
        <Button type="button" variant="outline" onClick={() => void check()} disabled={loading || !description.trim()}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
          {loading ? "Prüfung läuft …" : "Plausibilität prüfen"}
        </Button>
      </div>
      {!description.trim() && <p className="text-xs text-muted-foreground">Bitte zuerst eine Leistungsbeschreibung eingeben.</p>}
    </section>
    <Dialog open={open} onOpenChange={(next) => { if (!next) close(); }}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>KI-Plausibilitätsprüfung</DialogTitle>
          <DialogDescription>Unverbindlicher Abgleich der Leistungsbeschreibung mit LV- und Foto-Positionen, ohne Varianten. Das Prüfergebnis wird nicht gespeichert. Nur ausdrücklich übernommene Leistungen werden als LV-Positionen gespeichert. Die bisherigen Prüfungen bleiben davon unabhängig.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 space-y-4 overflow-y-auto pr-1" aria-live="polite">
          {loading && <p role="status" className="flex items-center gap-2 py-6"><Loader2 className="h-5 w-5 animate-spin" />Claude vergleicht Beschreibung und Positionen …</p>}
          {error && <p role="alert" className="rounded border border-red-300 bg-red-50 p-3 text-red-900">{error}</p>}
          {stale && !loading && <p className="rounded border border-orange-300 bg-orange-50 p-3 text-sm text-orange-950">Das Angebot wurde seit der Prüfung geändert. Das Ergebnis bezieht sich auf den vorherigen Stand. Bitte erneut prüfen.</p>}
          {empty && <p className="flex items-center gap-2 py-4 font-medium text-green-800"><CheckCircle2 className="h-5 w-5" />Keine Auffälligkeiten gefunden</p>}
          {rejected.length > 0 && <p role="alert" className="rounded border border-orange-300 bg-orange-50 p-3 text-sm text-orange-950">
            Die Prüfung ist unvollständig: {rejected.length} KI-Hinweis(e) konnten nicht belegt werden und werden separat angezeigt.
            {" "}Belegte Hinweise bleiben verfügbar. Nicht belegbare Hinweise können nicht ins LV übernommen werden. Bitte fachlich prüfen oder erneut prüfen.
          </p>}
          {result && hasFindings && categories.map(({ key, label, color, dot }) => <section key={key} className={`space-y-3 rounded-lg border p-4 ${color}`} aria-label={label}>
            <h4 className="flex items-center gap-2 font-bold"><span className={`h-3 w-3 rounded-full ${dot}`} aria-hidden="true" />{label} ({result[key].length})</h4>
            {result[key].length === 0 ? <p className="text-sm">Keine Hinweise in dieser Kategorie.</p> :
              <ul className="space-y-4">{result[key].map((entry, index) => {
                const position = "positionId" in entry ? snapshot?.positions.find((p) => p.id === entry.positionId) : undefined;
                const isMissing = key === "fehlt" && "leistung" in entry;
                const status = isMissing ? missingStatus(entry) : null;
                return <li key={index} className="space-y-1 text-sm">
                  {isMissing && canImport ? <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-primary"
                      aria-label={`Für LV-Übernahme auswählen: ${entry.leistung}`}
                      checked={selectedMissing.has(index)}
                      disabled={Boolean(status) || loading || importing || Boolean(stale)}
                      onChange={(event) => setSelectedMissing((current) => {
                        const next = new Set(current);
                        if (event.target.checked) next.add(index);
                        else next.delete(index);
                        return next;
                      })}
                    />
                    <span className="font-semibold">{entry.leistung}</span>
                  </label> : <p className="font-semibold">{"leistung" in entry ? entry.leistung : position?.kurztext ?? "Position"}</p>}
                  {status && <p className="text-xs font-medium" role="status">{status}</p>}
                  {position && <p>{position.menge} {position.einheit} · Teil {position.teil}</p>}
                  {"begruendung" in entry && <p>{entry.begruendung}</p>}
                  {"zitat" in entry && <blockquote className="whitespace-pre-wrap border-l-2 border-current pl-3 italic">„{entry.zitat}“</blockquote>}
                </li>;
              })}</ul>}
          </section>)}
          {rejected.length > 0 && <section className="space-y-3 rounded-lg border border-orange-300 bg-orange-50 p-4 text-orange-950" aria-label="Nicht belegbare KI-Hinweise">
            <h4 className="font-bold">Nicht belegbare KI-Hinweise ({rejected.length})</h4>
            <p className="text-sm">Nicht als bestätigte Befunde verwenden. Eine Übernahme ins LV ist gesperrt.</p>
            <ul className="space-y-4">{rejected.map(({ kategorie, hinweis, gruende }, index) => {
              const position = "positionId" in hinweis ? snapshot?.positions.find((p) => p.id === hinweis.positionId) : undefined;
              return <li key={`${kategorie}-${index}`} className="space-y-1 text-sm">
                <p className="font-semibold">{categories.find((category) => category.key === kategorie)?.label}: {"leistung" in hinweis ? hinweis.leistung : position?.kurztext ?? `Positionskennung: ${hinweis.positionId}`}</p>
                {gruende.includes("position_unbekannt") && <p>Die genannte Positionskennung gehört nicht zu den übergebenen Positionen.</p>}
                {gruende.includes("zitat_nicht_belegt") && <p>Das angegebene Zitat wurde auch unter Berücksichtigung von Formatunterschieden nicht in der Leistungsbeschreibung gefunden.</p>}
                {"begruendung" in hinweis && <p>{hinweis.begruendung}</p>}
                {"zitat" in hinweis && <p className="whitespace-pre-wrap">Nicht bestätigtes KI-Zitat: „{hinweis.zitat}“</p>}
              </li>;
            })}</ul>
          </section>}
          {result && canImport && result.fehlt.length > 0 && <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-4" aria-label="Fehlende Leistungen ins LV übernehmen">
            <h4 className="font-semibold">Ausgewählte Leistungen ins LV übernehmen</h4>
            <p className="text-sm text-muted-foreground">Neue LV-Zeilen werden mit 1 psch und Preis 0 angelegt. Menge und Preis können Sie anschließend manuell bearbeiten.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {importSections?.length === 1
                ? <div className="grid gap-1 text-sm"><span className="font-medium">LV-Abschnitt</span><span className="flex h-10 items-center rounded-md border border-input bg-background px-3">{importSections[0].title}</span></div>
                : <label className="grid gap-1 text-sm font-medium">
                  LV-Abschnitt
                  <select className="h-10 rounded-md border border-input bg-background px-3" value={sectionId} onChange={(event) => setSectionId(event.target.value)} disabled={importing}>
                    <option value="">Abschnitt auswählen</option>
                    {importSections?.map((section) => <option key={section.id} value={section.id}>{section.title}</option>)}
                  </select>
                </label>}
              <label className="grid gap-1 text-sm font-medium">
                Angebotsteil
                <select className="h-10 rounded-md border border-input bg-background px-3" value={teil} onChange={(event) => setTeil(event.target.value as MissingServicesImport["teil"] | "")} disabled={importing}>
                  <option value="">Teil auswählen</option>
                  <option value="A">Teil A</option>
                  <option value="B">Teil B</option>
                  <option value="C">Teil C</option>
                </select>
              </label>
            </div>
            {importError && <p role="alert" className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900">{importError}</p>}
            <Button type="button" onClick={() => void submitMissing()} disabled={selectedCount === 0 || !resolvedSectionId || !teil || loading || importing || Boolean(stale)}>
              {importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {importing ? "Übernahme läuft …" : `Ins LV übernehmen (${selectedCount})`}
            </Button>
          </section>}
          {result && <p className="flex gap-2 text-xs text-muted-foreground"><AlertTriangle className="h-4 w-4 shrink-0" />KI-Hinweise bitte fachlich prüfen. Eine vollständige Leistungsprüfung ist dadurch nicht garantiert.</p>}
        </div>
        <DialogFooter className="gap-2">
          {!loading && (error || result) && <Button type="button" variant="outline" onClick={() => void check()} disabled={!description.trim()}>Erneut prüfen</Button>}
          <Button type="button" onClick={close}>{loading ? "Abbrechen" : "Schließen"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
